import React, { useEffect, useRef } from 'react';
import { useTimer } from '@tanium/react-use-timer';
import { AdvancedTooltip } from './AdvancedTooltip';
import { TooltipManager } from './TooltipManager';
import { TooltipReference } from './TooltipReference';
import { ReferenceChildren, TooltipProps } from './types';

export interface Props {
  /**
   * Content to be displayed inside the tooltip.
   */
  content: React.ReactNode;
  /**
   * The component to be tooltipped. This render prop requires the inner component to assign the
   * supplied ref to itself. This means that functional components cannot receive tooltips because
   * they do not have instances with refs.
   */
  children: ReferenceChildren;
  /**
   * Placement of the tooltip. Follows popper's conventions. Note that placements are a "best
   * attempt," and if the underlying library detects that the tooltip will not render well, it will
   * try other placements.
   */
  placement?: TooltipProps['placement'];
  /**
   * Modifiers for the tooltip based on Popper's modifiers structure. If you find yourself using
   * this excessively, consider using AdvancedTooltip.
   */
  modifiers?: TooltipProps['modifiers'];

  /**
   * Optional, if specified it delays showing the tooltip by the specified `ms`
   */
  delayShowMs?: number;

  /**
   * Optional, if specified it delays hiding the tooltip by the specified `ms`
   */
  delayHideMs?: number;

  /**
   * Optional, if specified it adds specified opacity to the tooltip in dark mode.
   */
  opacity?: string;
}

export interface State {
  isHovered: boolean;
}

const mergeEventListeners = (first: EventListener, second: EventListener) => (e: Event) => {
  if (first) {
    first(e);
  }
  if (second) {
    second(e);
  }
};

/**
 * A minimal-configuration tooltip for general use.
 */
export const SimpleTooltip: React.FC<Props> = ({
  content,
  delayShowMs = 0,
  delayHideMs = 0,
  modifiers,
  placement,
  children,
  opacity,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isDismissed, setIsDismissed] = React.useState(false);

  const onKeyDown = useRef<(e: KeyboardEvent) => void>((e) => {
    if (e.key === 'Escape') {
      setIsDismissed(true);
    }
  });

  useEffect(() => {
    const onKeyDownValue = onKeyDown.current;

    document.addEventListener('keydown', onKeyDownValue);
    return () => {
      document.removeEventListener('keydown', onKeyDownValue);
    };
  }, []);

  const showTooltip = () => {
    setIsHovered(true);
    setIsDismissed(false);
  };

  const hideTooltip = () => {
    setIsHovered(false);
  };

  const { start: showTimerStart, stop: showTimerStop } = useTimer(showTooltip, delayShowMs);

  const { start: hideTimerStart, stop: hideTimerStop } = useTimer(hideTooltip, delayHideMs);

  const handleMouseOver = () => {
    hideTimerStop();
    if (!isHovered) {
      showTimerStart();
    }
  };

  const handleMouseOut = () => {
    showTimerStop();
    if (isHovered) {
      hideTimerStart();
    }
  };

  return (
    <TooltipManager>
      <TooltipReference>
        {(args) => {
          const Element = React.Children.only(children(args));
          return React.cloneElement(Element, {
            onMouseOver: mergeEventListeners(Element.props.onMouseOver, handleMouseOver),
            onMouseOut: mergeEventListeners(Element.props.onMouseOut, handleMouseOut),
          });
        }}
      </TooltipReference>
      {!isDismissed && isHovered && content && (
        <AdvancedTooltip modifiers={modifiers} placement={placement} opacity={opacity}>
          {content}
        </AdvancedTooltip>
      )}
    </TooltipManager>
  );
};
